import React, {useEffect, useRef, useState, useMemo} from "react";
import {observer} from "mobx-react-lite";
import "./eventBookingNotes.sass";
import {IconSource} from "../../../shared/enums/iconSource";
import Icon from "../../atoms/common/icon/icon";
import Tag from "../../atoms/tag/tag";
import {isNullOrEmptyOrWhitespace} from "../../../utils/stringUtils";
import {TextObservable} from "../../../stores/observables/textObservable";
import {useTranslation} from "react-i18next";
import classNames from "classnames";

export interface IEventBookingNotes {
  /**
   * The note for the booking, set by a staff member
   */
  staffNote?: string;
  /**
   * The note for the booking, set by the resident
   */
  residentNote?: string;
  /**
   * Called only when the input component looses focus, and the user has updated the note
   */
  onStaffNoteUpdated?: (staffNote?: string) => void;
  /**
   * Is the user prevented from editing the notes
   */
  preventEdit?: boolean;
  /**
   * The name of the creator of this booking. Will be present only for app bookings.
   */
  bookingOnBehalfCreatorName?: string;
}

const EventBookingNotes: React.FC<IEventBookingNotes> = ({
  staffNote,
  residentNote,
  onStaffNoteUpdated,
  preventEdit = false,
  bookingOnBehalfCreatorName
}) => {
  // The max number of characters allowed in a staff note
  const maxStaffNoteLength = 80;

  const [selected, setIsSelected] = useState(false);

  useEffect(() => {
    if (selected && preventEdit) {
      setIsSelected(false);
    }
  }, [selected, preventEdit]);

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const handleKeyPress = (event: any) => {
    if (
      event.key === "Escape" ||
      event.key === "Tab" ||
      event.key === "Enter"
    ) {
      event.preventDefault();
      setIsSelected(false);
      event.target.blur();
    }
  };

  useEffect(() => {
    if (selected) {
      inputRef.current?.focus();
    }
  }, [selected]);

  // The business rules state we only save the staff note when this component is blurred
  // For this reason, we must encapsulate the "onChange" on the
  // text observable inside this component to prevent it being accessed incorrectly
  const staffNoteObservable = useMemo(() => {
    return new TextObservable(staffNote ?? "", maxStaffNoteLength);
  }, [staffNote]);

  const residentNotesExist = !isNullOrEmptyOrWhitespace(residentNote);
  const staffNoteExist = !isNullOrEmptyOrWhitespace(staffNoteObservable.text);

  const {t} = useTranslation(["translation", "event"]);

  const onBlur = () => {
    const inputValue = isNullOrEmptyOrWhitespace(staffNoteObservable.text)
      ? undefined
      : staffNoteObservable.text.trim();

    if (staffNote === inputValue || (!staffNote && !inputValue)) {
      return;
    }

    onStaffNoteUpdated?.(inputValue?.substring(0, maxStaffNoteLength));
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (!(event.target.value.length > maxStaffNoteLength)) {
      staffNoteObservable.setText(event.target.value);
    }

    setTextareaHeight(event.target);
  };

  const onCellClick = () => {
    if (!preventEdit) {
      setIsSelected(true);
    }
  };

  useEffect(() => {
    if (inputRef.current) {
      setTextareaHeight(inputRef.current);
    }
  }, [staffNote]);

  const setTextareaHeight = (textarea: HTMLTextAreaElement) => {
    if (textarea) {
      textarea.style.height = `${textarea?.scrollHeight}px`;
    }
  };

  return (
    <div
      className={classNames("event-booking-notes", {
        "is-invalid": !staffNoteObservable.isValid
      })}
      onClick={onCellClick}
      onBlur={() => setIsSelected(false)}
    >
      {(residentNotesExist || !!bookingOnBehalfCreatorName) && (
        <div data-testid="resident-note">
          <Icon source={IconSource.lock} />
          <div>
            {!!bookingOnBehalfCreatorName && (
              <div>
                {t("event:labels.booking.bookedVia")}
                <span> {bookingOnBehalfCreatorName}</span>
              </div>
            )}
            <span>{residentNote}</span>
          </div>
        </div>
      )}
      {(staffNoteExist || selected) && (
        <div data-testid="staff-note">
          <Tag
            type="system"
            text={t("event:labels.bookingSourceShorthand.staff")}
          />
          <div className="grow-wrap">
            <textarea
              rows={1}
              value={staffNoteObservable.text}
              onChange={(event) => handleTextChange(event)}
              onBlur={onBlur}
              ref={inputRef}
              onKeyDown={handleKeyPress}
              disabled={preventEdit}
            />
            {staffNoteObservable.maxLength &&
              staffNoteObservable.text &&
              selected && (
                <div className="text-counter">
                  <div
                    className={classNames({
                      "has-error":
                        staffNoteObservable.maxLength ===
                        staffNoteObservable.text.length
                    })}
                    data-testid="textBoxCounter"
                  >
                    {`${staffNoteObservable.text.length}/${staffNoteObservable.maxLength}`}
                  </div>
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
};

export default observer(EventBookingNotes);
